import { createTheme, Theme } from '@mui/material/styles';

/**
 * Construye los temas MUI (dark y light) a partir de `src/theme/tokens.css`.
 *
 * POR QUE LEER EL CSS EN VEZ DE REPETIR LOS VALORES AQUI
 * ------------------------------------------------------
 * Repetir los ~30 colores en TypeScript para el tema de MUI y otra vez en CSS
 * para Tailwind garantiza que en unos meses los dos se desincronicen (es
 * justo el problema que tenia esta app: el navy `#161d28` estaba escrito a
 * mano en 79 sitios distintos).
 *
 * Leyendo las variables con `getComputedStyle` hay un unico origen de verdad:
 * quien cambia un color lo cambia en `tokens.css` y MUI y Tailwind lo recogen.
 *
 * EL PROBLEMA DEL TIEMPO DE LECTURA Y COMO SE RESUELVE
 * ---------------------------------------------------
 * `getComputedStyle(document.documentElement)` no sirve: el tema se construye
 * durante el render, y el atributo `data-theme` en `<html>` lo escribe el
 * provider en un efecto posterior. Leer del documento daria el tema anterior,
 * es decir, un cambio de tema con un render de retraso.
 *
 * Por eso se lee de una sonda: un div oculto al que se le pone el atributo
 * `data-theme` del tema que queremos medir. Como las variables CSS heredan,
 * la sonda devuelve los valores correctos del modo pedido sin depender del
 * estado del documento. Medirla no cuesta: `getComputedStyle` sobre un
 * elemento de 0x0 es barato y solo ocurre al cambiar de tema.
 */

export type ThemeMode = 'dark' | 'light';

/** Identificador de la sonda. No debe colisionar con nada de la app. */
const PROBE_ID = '__pmyl_theme_probe__';

interface Tokens {
  bg: string;
  surface: string;
  surfaceRaised: string;
  surfaceOverlay: string;
  text: string;
  textMuted: string;
  textSubtle: string;
  border: string;
  borderStrong: string;
  brand: string;
  brandStrong: string;
  brandHover: string;
  brandText: string;
  brandSoft: string;
  accent: string;
  accentStrong: string;
  accentHover: string;
  accentText: string;
  accentSoft: string;
  onAccent: string;
  onBrand: string;
  onState: string;
  textDisabled: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  fontSans: string;
  radius: string;
  radiusLg: string;
}

/**
 * Devuelve (creando si hace falta) la sonda y le fija el tema pedido.
 *
 * El estilo en linea oculta la sonda sin sacarla del arbol: los elementos con
 * `display: none` siguen teniendo estilos calculados, pero los que estan
 * fuera del documento no.
 */
const probeFor = (mode: ThemeMode): HTMLElement => {
  let probe = document.getElementById(PROBE_ID);

  if (!probe) {
    probe = document.createElement('div');
    probe.id = PROBE_ID;
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText =
      'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;opacity:0;';
    document.body.appendChild(probe);
  }

  probe.setAttribute('data-theme', mode);
  return probe;
};

/** Lee los tokens del modo indicado. Los `fallback` son la red de seguridad. */
const readTokens = (mode: ThemeMode): Tokens => {
  const computed = getComputedStyle(probeFor(mode));
  const v = (name: string, fallback: string) => computed.getPropertyValue(name).trim() || fallback;

  /** Se lee primero porque `onState` lo necesita para el contraste. */
  const bg = v('--bg', '#0a0f1a');

  return {
    bg,
    surface: v('--surface', '#151c2b'),
    surfaceRaised: v('--surface-2', '#1d2637'),
    surfaceOverlay: v('--surface-3', '#26313f'),
    text: v('--text', '#f5f7fa'),
    textMuted: v('--text-muted', '#a8b3c7'),
    textSubtle: v('--text-subtle', '#7c879c'),
    border: v('--border', '#2e3a4d'),
    borderStrong: v('--border-strong', '#56688a'),
    brand: v('--brand', '#5a85cf'),
    brandStrong: v('--brand-strong', '#3a67be'),
    brandHover: v('--brand-hover', '#85a6de'),
    brandText: v('--brand-text', '#85a6de'),
    brandSoft: v('--brand-soft', 'rgba(90, 133, 207, 0.16)'),
    accent: v('--accent', '#e545a3'),
    accentStrong: v('--accent-strong', '#c7158c'),
    accentHover: v('--accent-hover', '#ee74bc'),
    accentText: v('--accent-text', '#ee74bc'),
    accentSoft: v('--accent-soft', 'rgba(199, 21, 140, 0.16)'),
    onAccent: v('--on-accent', '#ffffff'),
    onBrand: v('--on-brand', '#ffffff'),
    /**
     * Texto que va encima de un color de estado (success/warning/error). Es
     * oscuro en tema oscuro y blanco en tema claro, porque los estados vivos
     * sobre negro piden texto oscuro y los estados oscurecidos de tema claro
     * piden texto blanco.
     */
    onState: mode === 'dark' ? bg : '#ffffff',
    textDisabled: v('--text-disabled', '#9ba3b5'),
    success: v('--success', '#43e97b'),
    warning: v('--warning', '#f7a93e'),
    danger: v('--danger', '#ff6b6b'),
    info: v('--info', '#85a6de'),
    fontSans: v('--font-sans', "'Poppins', sans-serif"),
    radius: v('--radius', '10px'),
    radiusLg: v('--radius-lg', '16px'),
  };
};

const px = (value: string, fallback: number): number => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const buildTheme = (mode: ThemeMode): Theme => {
  const t = readTokens(mode);
  const radius = px(t.radius, 10);
  const radiusLg = px(t.radiusLg, 16);

  return createTheme({
    palette: {
      mode,
      primary: {
        /**
         * El relleno usa `brandStrong`, no el navy puro. El navy del logo
         * (#1e458b) con texto blanco encima no llega a AA en modo oscuro; el
         * tono de la rampa que si llega da 5.44:1 con blanco (9.21:1 en claro).
         */
        main: t.brandStrong,
        /**
         * `light` es el tono que se usa para TEXTO e iconos sobre superficie.
         * El navy puro no sirve aqui: sobre la tarjeta oscura da 2.08:1. El
         * tono aclarado de la rampa da 6.90:1.
         */
        light: t.brandText,
        dark: t.brandStrong,
        contrastText: t.onBrand,
      },
      secondary: {
        /**
         * `secondary` es el color de CONVERSION (magenta de marca), no un segundo
         * azul. Antes era el mismo indigo que `primary`, asi que `color="secondary"`
         * no significaba nada.
         *
         * OJO: cualquier uso preexistente cambia de color. Los del repo se
         * re-semantizaron en la Task 5.
         */
        main: t.accentStrong,
        light: t.accentText,
        dark: t.accentStrong,
        contrastText: t.onAccent,
      },
      success: { main: t.success, contrastText: t.onState },
      warning: { main: t.warning, contrastText: t.onState },
      error: { main: t.danger, contrastText: t.onState },
      info: {
        /**
         * El `info` anterior era el mismo indigo claro que la marca, asi que no
         * se distinguia de un enlace. Ahora es el navy de marca: sigue siendo
         * el tono legible sobre superficie.
         */
        main: t.brandText,
        contrastText: t.onState,
      },
      background: {
        default: t.bg,
        paper: t.surface,
      },
      text: {
        primary: t.text,
        secondary: t.textMuted,
        disabled: t.textDisabled,
      },
      divider: t.border,
    },

    shape: { borderRadius: radius },

    typography: {
      fontFamily: t.fontSans,
      /**
       * Jerarquia por tamano y peso REALES. Antes la negrita era sintetica
       * porque los `@font-face` declaraban un unico archivo como weight 800.
       */
      h1: { fontSize: '30px', fontWeight: 600, lineHeight: 1.25 },
      h2: { fontSize: '24px', fontWeight: 600, lineHeight: 1.25 },
      h3: { fontSize: '20px', fontWeight: 600, lineHeight: 1.3 },
      h4: { fontSize: '18px', fontWeight: 600, lineHeight: 1.3 },
      h5: { fontSize: '17px', fontWeight: 600, lineHeight: 1.3 },
      h6: { fontSize: '15px', fontWeight: 600, lineHeight: 1.4 },
      body1: { fontSize: '15px', lineHeight: 1.5 },
      body2: { fontSize: '14px', lineHeight: 1.5 },
      button: { textTransform: 'none', fontWeight: 500 },
    },

    components: {
      /**
       * El campo de texto es el componente que mas roto estaba: su fondo
       * (#161d28) era MAS oscuro que la tarjeta que lo contenia (#1e293b), con
       * un contraste de 1.16:1. Se veia como un agujero, no como un campo.
       *
       * Ahora el campo sube de nivel (`surfaceRaised`, mas claro que la
       * tarjeta) y lleva borde visible. Al enfocar, el borde toma el color de
       * marca y aparece un anillo, que es la senal fuerte para quien navega con
       * teclado.
       */
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: t.surfaceRaised,
            borderRadius: radius,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: t.borderStrong,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: t.brand,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: t.brand,
              borderWidth: '2px',
            },
            '&.Mui-disabled': {
              backgroundColor: t.surfaceOverlay,
            },
            '& input::placeholder, & textarea::placeholder': {
              color: t.textSubtle,
              opacity: 1,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: t.textMuted,
            /** Al enfocar, el label es texto: necesita el tono legible. */
            '&.Mui-focused': { color: t.brandText },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: { color: t.textSubtle },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: { color: t.textMuted },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: radius,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            /**
             * Estado deshabilitado para CUALQUIER variante. Antes solo existia
             * en `containedPrimary`, asi que un boton outlined o de texto
             * deshabilitado caia en los grises por defecto de MUI (pensados
             * para tema claro) y se quedaba en 4.38:1 sobre el tema claro.
             *
             * `containedPrimary` lo redefine mas abajo porque ahi tambien
             * cambia el fondo.
             */
            '&.Mui-disabled': { color: t.textDisabled },
          },
          /**
           * El boton deshabilitado no puede verse igual que el habilitado.
           * Antes `RoundedButton` pintaba los dos con el mismo `#03a9f4`, asi
           * que no habia forma de saber si el formulario estaba listo.
           */
          containedPrimary: {
            backgroundColor: t.brandStrong,
            color: t.onBrand,
            '&:hover': { backgroundColor: t.brandHover },
            '&.Mui-disabled': {
              backgroundColor: t.surfaceOverlay,
              color: t.textDisabled,
            },
          },
          /**
           * El CTA del embudo. Antes no existia y caia en el `containedSecondary`
           * por defecto de MUI, que no conoce nuestros tokens.
           */
          containedSecondary: {
            backgroundColor: t.accentStrong,
            color: t.onAccent,
            '&:hover': { backgroundColor: t.accentHover },
            '&.Mui-disabled': {
              backgroundColor: t.surfaceOverlay,
              color: t.textDisabled,
            },
          },
          outlined: {
            borderColor: t.borderStrong,
            color: t.text,
            '&:hover': { borderColor: t.brand, backgroundColor: t.surfaceRaised },
          },
          text: {
            /** Boton de texto: es texto plano, va con el tono legible. */
            color: t.brandText,
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: t.surface,
            backgroundImage: 'none',
            border: `1px solid ${t.border}`,
            borderRadius: radiusLg,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: t.surface,
            backgroundImage: 'none',
            border: `1px solid ${t.border}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: t.surface,
            backgroundImage: 'none',
            borderColor: t.border,
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: t.border },
          head: { color: t.textMuted, fontWeight: 600 },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: t.border } },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: radius },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { backgroundColor: t.surfaceOverlay },
          bar: { backgroundColor: t.brand },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: t.surfaceOverlay,
            color: t.text,
            border: `1px solid ${t.border}`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: t.brandSoft,
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: { color: t.brandText },
        },
      },
    },
  });
};
